"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnitHeartbeatRef = exports.SipUnitDeclarationBase = exports.UnitDeclarationBase = exports.UnitPlatformSip = exports.UnitSipTransport = exports.UnitKind = exports.UnitId = void 0;
const zod_1 = require("zod");
/**
 * Стабильная identity независимого юнита платформы — SBC-контура или
 * Communication Unit (UNITREG O7).
 *
 * Одна граница на обе оси намеренно: из `unitId` строятся имя PJSIP-endpoint'а,
 * имя секции конфига и токен NATS-subject'а. Значение с точкой даёт лишний
 * токен в subject (адресная команда уедет мимо владельца), значение с `*`/`>` —
 * wildcard, значение с переводом строки — произвольную секцию в pjsip.conf.
 * Поэтому charset ограничен здесь, а не проверками по месту использования.
 *
 * `SbcUnitId` (`@omniflow/contracts-sbc`) — тот же charset; исторически он
 * появился первым и остаётся своим именем ради читаемости доменных схем.
 */
exports.UnitId = zod_1.z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_-]*$/);
/**
 * Ось юнита. `sbc` — сетевой контур (Kamailio + sbc-agent), `cu` —
 * media-engine (Asterisk + cu-agent), `rtpe` — медиа-прокси (rtpengine +
 * rtpe-agent, эпик RTPE O1). Оси независимы: одинаковые `unitId` в разных
 * `kind` допустимы, ключ реестра — пара `(kind, unitId)`.
 */
exports.UnitKind = zod_1.z.enum(['sbc', 'cu', 'rtpe']);
/** Транспорт SIP-плеча, по которому платформа говорит сама с собой. */
exports.UnitSipTransport = zod_1.z.enum(['udp', 'tcp', 'tls', 'ws', 'wss']);
/**
 * Внутреннее SIP-плечо юнита (UNITREG O8): internal-listener Kamailio у SBC,
 * PJSIP-транспорт Asterisk у CU. Это адрес, по которому юниты платформы
 * говорят ДРУГ С ДРУГОМ, — не публичный периметр.
 *
 * Имя `internal` не используется: в проекте оно занято смыслом «межсервисный
 * subject, недостижимый снаружи».
 */
exports.UnitPlatformSip = zod_1.z.object({
    host: zod_1.z.string().min(1).max(255),
    port: zod_1.z.number().int().positive().max(65535),
    transport: exports.UnitSipTransport,
});
/**
 * Общий предок деклараций всех осей (UNITREG O7). Юнит объявляет только
 * физику — адреса, порты, транспорты, версии (O1); бизнес-привязки (какой
 * транк какого тенанта где живёт) остаются данными Core.
 *
 * Поля порядка и дедупа разведены намеренно (O16):
 *   • `declVersion` — sha содержимого, НЕ монотонен, годится только для дедупа;
 *   • `declSeq` — монотонный счётчик в пределах процесса;
 *   • `instanceId` + `startedAt` — чтобы сравнить декларации РАЗНЫХ воплощений
 *     одного юнита: переставленная доставка иначе записала бы старую
 *     декларацию как текущую и разослала бы её соседям.
 *
 * Строки ограничены `.max()` не для красоты: событие сверх границы схемы
 * подписчик дропает молча (`packages/utils/src/nats.ts`).
 */
exports.UnitDeclarationBase = zod_1.z.object({
    unitId: exports.UnitId,
    declVersion: zod_1.z.string().min(1).max(128),
    declSeq: zod_1.z.number().int().nonnegative(),
    instanceId: zod_1.z.string().uuid(),
    startedAt: zod_1.z.string().datetime(),
    serviceVersion: zod_1.z.string().min(1).max(64),
});
/**
 * Предок юнитов с SIP-плечом (sbc, cu). Вынесен из общего предка эпиком RTPE
 * (O1): у медиа-прокси SIP-адреса нет, а требовать его «пустым» значило бы
 * врать реестру. Wire-форма sbc/cu-деклараций не изменилась.
 */
exports.SipUnitDeclarationBase = exports.UnitDeclarationBase.extend({
    platformSip: exports.UnitPlatformSip,
});
/**
 * Компактная отметка юнита в heartbeat'е (UNITREG O3): «я жив, и вот версия
 * моей декларации». Адресов здесь нет — за ними реестр идёт догоном на
 * доменный subject юнита.
 *
 * ⚠ Реестр heartbeat'у НЕ доверяет (O9): pub-право на `evt.health.heartbeat`
 * есть и у роли `strategy`, поэтому отсюда регистрация нового юнита не
 * происходит — только сигнал «есть версия новее».
 */
exports.UnitHeartbeatRef = zod_1.z.object({
    kind: exports.UnitKind,
    unitId: exports.UnitId,
    declVersion: zod_1.z.string().min(1).max(128),
});
//# sourceMappingURL=unit.js.map